<template>
  <v-container>
    <v-form>
      <div v-if="isEditMode" class="d-flex my-4 justify-start align-center">
        <v-spacer></v-spacer>
        <h4 class="mr-3"> {{ $tc('contract.status', 1) }}: </h4>
          <span class="text--secondary mr-1">{{contract.active ? $t('contract.active') : $t('contract.inactive')}}</span>
          <v-badge
            bordered
            dot
            inline
            floating
            :class="contract.active ? 'glow-badge-success' : 'glow-badge-error'"
            :color="contract.active ? 'success' : 'error'"
          ></v-badge>
          <div class="mx-3">
            <v-btn
              rounded
                dark
                dense
                block
                small
                color="success"
                @click="handleClickToggleStatus"
              >
                {{ contract.active ? $t('contract.deactivate') : $t('contract.activate') }}
              </v-btn>
            </div>
          </div>
      <v-sheet>
        <v-row v-if="!form.isParent">
          <v-col cols="12">
            <h4>{{ $tc('contract.party', 2)}}</h4>
          </v-col>
          <v-col cols="6">
            <AddressList
              :label="$tc('contract.employer', 1)"
              v-model="form.employer"
              :items="employers"
              @addNew="handleAddEmployer"
              :error-messages="errorMessages(this.$v.form.employer, 'Employer')"
            />
          </v-col>
          <v-col cols="6">
            <AddressList
              :label="$tc('contract.contractor', 1)"
              v-model="form.contractor"
              :items="contractors"
              @addNew="handleAddContractor"
              :error-messages="errorMessages(this.$v.form.contractor, 'Contractor')"
            />
          </v-col>
        </v-row>
        <component
          :is="form.isParent ? 'ParentInfoSection' : 'InfoSection'"
          :form="form"
          :validation-model="$v"
          :error-messages="errorMessages"
          :projects="projects"
          :packages="packages"
          :types="contractTypes"
          :areas="areas"
          @OPEN_PARENT="handleGoToParent"
          :is-multi-currency="form.isMultiCurrency"
        />
        <PartsSection
          v-if="!form.isParent"
          :form="form"
          :currencies="currenciesCodes"
          :validation-model="$v"
          :error-messages="errorMessages"
          :is-multi-currency="form.isMultiCurrency"
          @setUseMultiCurrency="() => form.isMultiCurrency = !form.isMultiCurrency"
          @calculate-percents="calculatePartsPercents"
        />
        <v-row v-if="!form.isParent">
          <v-col cols="8">
            <FinancesSection
              :form="form"
              @remove="removeCurrency"
              :currencies="currenciesCodes"
              :validation-model="$v"
              :error-messages="errorMessages"
              :is-multi-currency="form.isMultiCurrency"
              @calculate-percents="calculatePartsPercents"
            />
          </v-col>

          <v-col>
            <TimingSection
              :form="form"
              :validation-model="$v"
              :error-messages="errorMessages"
            />
          </v-col>
        </v-row>

        <v-row v-if="!form.isParent">
          <v-col>
            <v-text-field
              v-model="form.priceBreakdown"
              outlined
              prepend-inner-icon="mdi-file-link-outline"
              :label=" $t('contract.priceBreakDown')"
              :hint="$t('contract.sharepointLinkHint')"
              persistent-hint
              @change="$v.form.priceBreakdown.$touch()"
              :error-messages="errorMessages(this.$v.form.priceBreakdown, 'priceBreakdown')"
            />
          </v-col>
        </v-row>
        <v-row class="justify-center">
          <v-alert
            v-if="parentHasDataWarning"
            type="warning"
            dense
            outlined
          >
            {{parentHasDataWarning}}
          </v-alert>
        </v-row>
        <v-row align="end">
          <v-spacer />
          <v-col
            cols="12"
            md="2"
          >
            <v-btn
              rounded
              dark
              dense
              block
              outlined
              color="primary"
              @click="cancel"
            >
              {{ $t('common.cancel') }}
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            md="2"
          >
            <div class="error--text text-center">{{contractHaveErrors}}</div>
            <SubmitButton
              @click="submit"
              block
              :disabled="!dirty || !!contractHaveErrors"
            />
          </v-col>
        </v-row>
      </v-sheet>
    </v-form>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { errorMessageGenerator, contractModel, parentContractModel } from '@/validators'
import _ from 'lodash'
import { createNewContract, updateContract, getContracts, toggleContractStatus } from '@/services'
import { DEFAULT_CONTRACT, DEFAULT_PART } from '@/helpers'
import { authContext } from '@/plugins/msalPlugin'
import { currenciesCodes } from '@/store/getters'

export default {
  name: 'ContractForm',
  props: {
    contract: {
      type: Object,
      default: () => ({}),
      required: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      contracts: (state) => state.contracts,
      projects: (state) => state.projects,
      packages: (state) => state.packages,
      contractTypes: (state) => state.contractTypes,
      employers: (state) => state.employers,
      contractors: (state) => state.contractors,
      currencies: (state) => state.currencies,
      areas: (state) => state.areas
    }),
    ...mapGetters([currenciesCodes]),
    contractHaveErrors () {
      return this.$v.$error ? this.$t('contract.hasErrors') : null
    },
    createNewContractPage () {
      return this.$route.name === 'new contract'
    },
    employer () {
      return this.form.employer
    },
    contractor () {
      return this.form.contractor
    },
    parentHasDataWarning () {
      const hasValues = this.form.employer || this.form.contractor || this.form.contractType
      if (this.form.isParent && hasValues) {
        return this.$t('contract.parentHasDataWarning')
      }
      return null
    }
  },
  async mounted () {
    !this.contracts.length && await getContracts()
  },
  data () {
    const areaCodesWithProjects = new Set(this.$store.state.projects.flatMap((p) => p.areas.map((a) => a.code)))
    const defaultArea = this.$store.state.areas.find((a) => areaCodesWithProjects.has(a.code)) || this.$store.state.areas[0]
    const defaultContract = {
      ...DEFAULT_CONTRACT,
      packageManager: {},
      projectController: null,
      contractManager: authContext.account.username.split('@')[0],
      currencies: [{ key: this.$store.state.currencies[0].code, value: 1 }],
      area: defaultArea,
      isParent: this.contract.technicalType === 'PARENT',
      isMultiCurrency: this.contract.id ? true : this.contract.technicalType === 'PARTS_PERCENT',
      parts: [{ ...DEFAULT_PART }],
      signedPriceCents: 0,
      liquidatedDamagesCap: {
        valueCents: null,
        valuePercent: null,
        currency: this.$store.state.currencies[0].code
      }
    }
    return {
      form: Object.assign(
        { contractManager: {}, packageManager: {}, projectController: {} },
        _.cloneDeep(defaultContract),
        _.cloneDeep(this.contract)
      ),
      dirty: false
    }
  },
  methods: {
    handleGoToParent () {
      this.$router.push({ name: 'contract', params: { contractId: this.form.parentContractId } })
      this.cancel()
    },
    errorMessages (model, key) {
      return errorMessageGenerator(model, key)
    },
    removeCurrency (i) {
      this.form.currencies.splice(i, 1)
    },
    handleAddEmployer (search) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'EmployerForm',
        options: { maxWidth: 650 },
        scope: {
          value: { companyName: search }
        },
        handlers: {
          setnewemployer: this.setEmployerFromDialog
        }
      })
    },
    handleAddContractor (search) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'ContractorForm',
        options: { maxWidth: 650 },
        scope: {
          value: { companyName: search }
        },
        handlers: {
          setnewcontractor: this.setContractorFromDialog
        }
      })
    },
    async submit () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const payload = {
          ..._.cloneDeep(this.form),
          // AD fields
          contractManager: this.form.isParent ? '' : this.form.contractManager.mailNickname || this.form.contractManager,
          packageManager: this.form.isParent ? '' : this.form.packageManager.mailNickname || this.form.packageManager,
          projectController: this.form.isParent ? '' : (this.form.projectController ? this.form.projectController.mailNickname || this.form.projectController : null),
          liquidatedDamagesCap: this.form.liquidatedDamagesCap,
          subContracts: this.form.isParent ? this.form.subContracts : []
        }
        payload.parts.forEach(i => {
          i.contractorId = i.contractor ? i.contractor.id : this.form.contractor.id
        })
        if (this.contract.id) {
          await updateContract(payload)
        } else {
          const contract = await createNewContract(payload)
          this.$router.push({
            name: 'contract',
            params: { contractId: contract.id }
          })
        }
        this.$eventBus.$emit('FETCH_CONTRACT')
        this.$eventBus.$emit('UNSET_DIRTY')
        this.$emit('toggle-edit')
        this.$v.$reset()
        const defaultContract = {
          ...DEFAULT_CONTRACT,
          packageManager: '',
          projectController: '',
          currencies: [{ key: this.$store.state.currencies[0].code, value: 1 }],
          parts: [{ ...DEFAULT_PART }]
        }
        this.form = Object.assign(
          { contractManager: {}, packageManager: {}, projectController: {} },
          _.cloneDeep(defaultContract),
          _.cloneDeep(this.contract)
        )
      }
    },
    cancel () {
      this.$v.$reset()
      const defaultContract = {
        ...DEFAULT_CONTRACT,
        packageManager: '',
        projectController: '',
        currencies: [{ key: this.$store.state.currencies[0].code, value: 1 }]
      }
      this.form = Object.assign(
        { contractManager: {}, packageManager: {}, projectController: {} },
        _.cloneDeep(defaultContract),
        _.cloneDeep(this.contract)
      )
      this.$eventBus.$emit('UNSET_DIRTY')
      this.createNewContractPage
        ? this.$router.push({ name: 'contracts' })
        : this.$emit('toggle-edit')
    },
    async handleClickToggleStatus () {
      await toggleContractStatus(this.form.id)
      this.$eventBus.$emit('FETCH_CONTRACT')
    },
    setEmployerFromDialog (item) {
      this.dirty = true
      this.form.employer = item
    },
    setContractorFromDialog (item) {
      this.dirty = true
      this.form.contractor = item
    },
    calculatePartsPercents () {
      const sumForCurrency = this.form.parts.reduce((acc, curr) => {
        acc[curr.currency] ? acc[curr.currency] += curr.signedPriceCents : acc[curr.currency] = curr.signedPriceCents
        const currency = this.form.currencies.find(i => i.key === curr.currency) || { value: 1 }
        acc.sum += curr.signedPriceCents * currency.value
        return acc
      }, { sum: 0 })
      this.form.parts.forEach(part => {
        const currency = this.form.currencies.find(i => i.key === part.currency) || { value: 1 }
        const sum = sumForCurrency.sum || part.signedPriceCents
        part.ratio = Math.round(((part.signedPriceCents * currency.value) / sum) * 10000) / 100 || 100
      })
    }
  },
  watch: {
    'form.contractor' (v, p) {
      this.form.parts = this.form.parts.map(i => {
        if ((i.contractor && i.contractor.id === p.id) || !i.contractor) {
          i.contractor = { ...v }
        }
        return i
      })
    },
    'form.isMultiCurrency' (v, p) {
      if (v && !p) {
        delete this.form.signedPriceCents
      } else {
        this.form.signedPriceCents = 0
      }
    },
    form: {
      deep: true,
      handler (v) {
        // check if contract fields (except packageManager and contractManager) was changed
        const formIsDirty = !_.isEqual(
          { ...v, packageManager: undefined, contractManager: undefined, projectController: undefined },
          {
            ...this.contract,
            packageManager: undefined,
            contractManager: undefined,
            projectController: undefined
          }
        )
        // check if managers was changed
        const packageManagerIsDirty = v.packageManager &&
          this.contract.packageManager !== (v.packageManager.mailNickname || v.packageManager)
        const projectControllerIsDirty = v.projectController &&
          this.contract.projectController !== (v.projectController.mailNickname || v.projectController)
        const contractManagerIsDirty = v.contractManager &&
          this.contract.contractManager !== (v.contractManager.mailNickname || v.contractManager)
        this.dirty = formIsDirty || packageManagerIsDirty || contractManagerIsDirty || projectControllerIsDirty
      }
    },
    dirty (v) {
      v && this.$eventBus.$emit('SET_DIRTY')
    },
    employer (v, p) {
      if (v !== p) {
        this.dirty = true
        this.$v.form.employer.$touch()
      }
    },
    contractor (v, p) {
      if (v !== p) {
        this.dirty = true
        this.$v.form.contractor.$touch()
      }
    }
  },
  validations () {
    return this.form.isParent ? parentContractModel : contractModel
  }
}
</script>
